import React from 'react';
import { act, render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';
import axios from 'axios';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import StudentSeatToggle from '../StudentSeatToggle';

jest.mock('axios');
const mockedAxios = axios as jest.Mocked<typeof axios>;

const createWrapper = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { retry: false },
      mutations: { retry: false },
    },
  });
  return ({ children }: { children: React.ReactNode }) => (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};

describe('StudentSeatToggle', () => {
  beforeEach(() => {
    jest.clearAllMocks();
  });

  it('renders with initial value true', () => {
    render(
      <StudentSeatToggle
        initialValue
        attribute="audio_enabled"
        studentSeatId={123}
      />,
      { wrapper: createWrapper() },
    );

    const checkbox = screen.getByRole('checkbox');
    expect(checkbox).toBeChecked();
  });

  it('renders with initial value false', () => {
    render(
      <StudentSeatToggle
        attribute="spanish_enabled"
        initialValue={false}
        studentSeatId={456}
      />,
      { wrapper: createWrapper() },
    );

    const checkbox = screen.getByRole('checkbox');
    expect(checkbox).not.toBeChecked();
  });

  it('toggles the checkbox and calls API on click', async () => {
    const user = userEvent.setup();
    const mockUpdatedAt = '2026-05-29T10:30:00Z';
    mockedAxios.patch.mockResolvedValueOnce({
      data: { data: { updated_at: mockUpdatedAt } },
    });

    render(
      <StudentSeatToggle
        attribute="reading_level_a_enabled"
        initialValue={false}
        studentSeatId={789}
      />,
      { wrapper: createWrapper() },
    );

    const checkbox = screen.getByRole('checkbox');
    expect(checkbox).not.toBeChecked();

    await user.click(checkbox);

    expect(checkbox).toBeChecked();
    await waitFor(() => {
      expect(mockedAxios.patch).toHaveBeenCalledWith(
        '/api/v2/student_seats/789',
        { student_seat: { reading_level_a_enabled: true } },
      );
    });
  });

  it('reverts the toggle on API error', async () => {
    const user = userEvent.setup();
    const consoleError = jest.spyOn(console, 'error').mockImplementation();
    mockedAxios.patch.mockRejectedValueOnce(new Error('Network error'));

    render(
      <StudentSeatToggle
        initialValue
        attribute="main_ideas_enabled"
        studentSeatId={321}
      />,
      { wrapper: createWrapper() },
    );

    const checkbox = screen.getByRole('checkbox');
    expect(checkbox).toBeChecked();

    await user.click(checkbox);

    await waitFor(() => {
      expect(checkbox).toBeChecked();
    });

    expect(consoleError).toHaveBeenCalledWith(
      'Error updating main_ideas_enabled:',
      expect.any(Error),
    );

    consoleError.mockRestore();
  });

  it('disables interaction while loading', async () => {
    const user = userEvent.setup();
    mockedAxios.patch.mockImplementation(
      () => new Promise(resolve => setTimeout(resolve, 100)),
    );

    render(
      <StudentSeatToggle
        attribute="audio_enabled"
        initialValue={false}
        studentSeatId={111}
      />,
      { wrapper: createWrapper() },
    );

    const checkbox = screen.getByRole('checkbox');

    await user.click(checkbox);

    expect(checkbox).toBeDisabled();
  });

  it('updates the timestamp container on successful update', async () => {
    const user = userEvent.setup();
    const mockUpdatedAt = '2026-05-29T15:45:30Z';
    mockedAxios.patch.mockResolvedValueOnce({
      data: { data: { updated_at: mockUpdatedAt } },
    });

    const timeContainer = document.createElement('div');
    timeContainer.id = 'updated-time-container';
    document.body.appendChild(timeContainer);

    render(
      <StudentSeatToggle
        attribute="audio_enabled"
        initialValue={false}
        studentSeatId={555}
      />,
      { wrapper: createWrapper() },
    );

    const checkbox = screen.getByRole('checkbox');
    await user.click(checkbox);

    await waitFor(() => {
      expect(timeContainer.innerHTML).toContain('Autosaved on:');
      expect(timeContainer.innerHTML).toContain('May');
      expect(timeContainer.innerHTML).toContain('2026');
    });

    document.body.removeChild(timeContainer);
  });

  it('handles each attribute type correctly', async () => {
    const user = userEvent.setup();
    const attributes = [
      'reading_level_a_enabled',
      'spanish_enabled',
      'audio_enabled',
      'main_ideas_enabled',
    ] as const;

    for (const attribute of attributes) {
      mockedAxios.patch.mockResolvedValueOnce({ data: {} });

      const { unmount } = render(
        <StudentSeatToggle
          attribute={attribute}
          initialValue={false}
          studentSeatId={999}
        />,
        { wrapper: createWrapper() },
      );

      const checkbox = screen.getByRole('checkbox');
      await user.click(checkbox);

      await waitFor(() => {
        expect(mockedAxios.patch).toHaveBeenCalledWith(
          '/api/v2/student_seats/999',
          { student_seat: { [attribute]: true } },
        );
      });

      unmount();
      jest.clearAllMocks();
    }
  });

  it('syncs checkbox from Zustand store update', async () => {
    const useStudentSeatSyncStore = require('@/stores/useStudentSeatSyncStore').default;

    render(
      <StudentSeatToggle initialValue attribute="reading_level_a_enabled" studentSeatId={789} />,
      { wrapper: createWrapper() },
    );

    act(() => {
      useStudentSeatSyncStore.getState().notify({
        readingLevelAEnabled: false,
        studentSeatId: '789',
      });
    });

    await waitFor(() => {
      expect(screen.getByRole('checkbox')).not.toBeChecked();
    });
  });

  it('notifies Zustand store with correct payload after toggle', async () => {
    const user = userEvent.setup();
    const useStudentSeatSyncStore = require('@/stores/useStudentSeatSyncStore').default;

    mockedAxios.patch.mockResolvedValueOnce({
      data: { data: { reading_level_a_enabled: true, reading_level: 'b', updated_at: '2026-01-01T00:00:00Z' } },
    });

    render(
      <StudentSeatToggle attribute="reading_level_a_enabled" initialValue={false} studentSeatId={789} />,
      { wrapper: createWrapper() },
    );

    await user.click(screen.getByRole('checkbox'));

    await waitFor(() => {
      expect(useStudentSeatSyncStore.getState().lastUpdate).toEqual({
        readingLevel: 'b',
        readingLevelAEnabled: true,
        studentSeatId: '789',
      });
    });
  });
});
